import { useState, useEffect } from 'react';
import axios from 'axios';
import { Pencil, X, Briefcase, Phone as PhoneIcon, Mail, ChevronRight, EyeOff, Key } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import PhotoCapture from './PhotoCapture';
import ChangeEmailDialog from './ChangeEmailDialog';
import { formatPhone } from '../lib/phone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg border text-sm outline-none';
const INPUT_STYLE = { borderColor: '#A3D4D4', fontFamily: 'Manrope', backgroundColor: 'white' };
const LABEL_CLASS = 'block text-[10px] font-bold uppercase tracking-wider mb-1';
const LABEL_STYLE = { fontFamily: 'Manrope', color: '#4A5D5D' };

const EMPTY_FORM = { name: '', title: '', phone: '', phone_fmt: '', bio: '', photo_url: '', linkedin_url: '', hide_email: false, hide_phone: false };

// Self-service profile editor for signed-in users. Reads /api/auth/me on
// open, lets the user update non-privileged fields (name, title, phones,
// bio, photo with camera + AI enhance), and saves via PUT /api/auth/me.
// Email and access role cannot be changed here.
export default function MyProfileDialog({ open, onClose }) {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [me, setMe] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  // iter-86 — self-service email change opens a nested 2-step dialog.
  const [emailChangeOpen, setEmailChangeOpen] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const auth = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/auth/me`, auth)
      .then(({ data }) => {
        if (cancelled) return;
        setMe(data);
        setForm({
          name:      data.name      || '',
          title:     data.title     || '',
          phone:     data.phone     || '',
          phone_fmt: data.phone_fmt || '',
          bio:       data.bio       || '',
          photo_url: data.photo_url || '',
          linkedin_url: data.linkedin_url || '',
          hide_email: !!data.hide_email,
          hide_phone: !!data.hide_phone,
        });
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Could not load profile');
        onClose();
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    if (!me) return;
    const patch = {};
    for (const k of Object.keys(form)) {
      // For boolean fields, compare !==; for strings, the existing logic.
      if (typeof form[k] === 'boolean') {
        if (form[k] !== !!me[k]) patch[k] = form[k];
      } else if ((form[k] || '') !== (me[k] || '')) {
        patch[k] = form[k] || '';
      }
    }
    if (Object.keys(patch).length === 0) {
      toast.info('No changes to save');
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.put(`${API}/auth/me`, patch, auth);
      setMe(data);
      toast.success('Profile updated');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally { setSubmitting(false); }
  };

  const initials = (form.name || me?.email || '?')
    .split(/[ @]+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="my-profile-dialog" className="w-[95vw] max-w-xl p-0 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#F4F1EB' }}>
        <div className="p-5 sm:p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
              <Pencil className="w-5 h-5" style={{ color: '#003DA5' }} />
              My profile
            </DialogTitle>
            {me && (
              <p className="text-xs mt-1" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
                Signed in as <span style={{ color: '#1A2E2E', fontWeight: 700 }}>{me.email}</span>
              </p>
            )}
          </DialogHeader>

          {loading ? (
            <div className="py-12 text-center text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              Loading your profile…
            </div>
          ) : (
            <>
              {/* Photo capture row */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 rounded-lg mb-4" style={{ backgroundColor: 'white' }}>
                <PhotoCapture
                  value={form.photo_url}
                  onChange={(url) => setForm((f) => ({ ...f, photo_url: url }))}
                  token={token}
                  size={88}
                  accent="#003DA5"
                  initials={initials}
                  testIdPrefix="my-profile-photo"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                    Profile picture
                  </p>
                  <p className="text-xs" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                    Take a photo with your device camera, upload an image, or polish it with AI for a clean professional headshot. Saves automatically when captured.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={LABEL_CLASS} style={LABEL_STYLE}>Display name</label>
                  <input
                    data-testid="my-profile-name"
                    className={INPUT_CLASS}
                    style={INPUT_STYLE}
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Andres Mocny"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL_CLASS} style={LABEL_STYLE}>
                    <Briefcase className="w-3 h-3 inline mr-1 -mt-0.5" /> Job title
                  </label>
                  <input
                    data-testid="my-profile-title"
                    className={INPUT_CLASS}
                    style={INPUT_STYLE}
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. General Manager"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS} style={LABEL_STYLE}>
                    <PhoneIcon className="w-3 h-3 inline mr-1 -mt-0.5" /> Phone (E.164)
                  </label>
                  <input
                    data-testid="my-profile-phone"
                    className={INPUT_CLASS}
                    style={INPUT_STYLE}
                    value={form.phone}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, phone: v, phone_fmt: formatPhone(v) || f.phone_fmt }));
                    }}
                    placeholder="+18763531980"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS} style={LABEL_STYLE}>Display phone</label>
                  <input
                    data-testid="my-profile-phone-fmt"
                    className={INPUT_CLASS}
                    style={INPUT_STYLE}
                    value={form.phone_fmt}
                    onChange={(e) => setForm((f) => ({ ...f, phone_fmt: e.target.value }))}
                    placeholder="+1 (876) 353-1980"
                  />
                  {form.phone && !form.phone_fmt && (
                    <p className="text-[10px] mt-1 italic" style={{ color: '#7A8C8C' }}>
                      Auto: {formatPhone(form.phone)}
                    </p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL_CLASS} style={LABEL_STYLE}>Bio</label>
                  <textarea
                    data-testid="my-profile-bio"
                    className={INPUT_CLASS + ' resize-none'}
                    style={{ ...INPUT_STYLE, minHeight: '72px' }}
                    rows={3}
                    value={form.bio}
                    onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                    placeholder="A short bio that appears on your public team card."
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL_CLASS} style={LABEL_STYLE}>
                    <Mail className="w-3 h-3 inline mr-1 -mt-0.5" /> Email (login)
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ borderColor: '#A3D4D4', backgroundColor: 'white' }}>
                    <span className="flex-1 text-sm truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }} data-testid="my-profile-email">
                      {me?.email || '—'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setEmailChangeOpen(true)}
                      data-testid="profile-change-email-open"
                      className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-md hover:opacity-90 transition-opacity"
                      style={{ background: '#003DA5', color: 'white', fontFamily: 'Manrope' }}
                    >
                      Change <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-[10px] mt-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                    Changing your email requires confirming a code sent to the new address. Your session will be reset.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL_CLASS} style={LABEL_STYLE}>LinkedIn URL</label>
                  <input
                    data-testid="my-profile-linkedin"
                    className={INPUT_CLASS}
                    style={INPUT_STYLE}
                    value={form.linkedin_url}
                    onChange={(e) => setForm((f) => ({ ...f, linkedin_url: e.target.value }))}
                    placeholder="https://linkedin.com/in/your-handle"
                  />
                </div>

                {/* iter-92 — Privacy toggles. Lets the signed-in user
                    hide their email and/or phone number from the public
                    "People behind the rebuild" team card. Admins still
                    see the real values; everyone else sees a clean
                    empty row (no contact, no unmask button). */}
                <div className="sm:col-span-2 p-4 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid #EAE5D9' }} data-testid="my-profile-privacy">
                  <div className="flex items-start gap-2 mb-3">
                    <EyeOff className="w-3.5 h-3.5 mt-0.5" style={{ color: '#1F4FBE' }} />
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                        Privacy — public team card
                      </p>
                      <p className="text-[11px] mt-0.5" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                        Choose what's shown on the &ldquo;People behind the rebuild&rdquo; section of the public landing page. Admins always see your real info.
                      </p>
                    </div>
                  </div>

                  <label className="flex items-center justify-between py-2 cursor-pointer">
                    <span className="text-sm" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>Hide my email address</span>
                    <PrivacyToggle
                      checked={form.hide_email}
                      onChange={(v) => setForm((f) => ({ ...f, hide_email: v }))}
                      testid="my-profile-hide-email"
                    />
                  </label>
                  <label className="flex items-center justify-between py-2 cursor-pointer border-t" style={{ borderColor: '#F1F5F9' }}>
                    <span className="text-sm" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>Hide my phone number</span>
                    <PrivacyToggle
                      checked={form.hide_phone}
                      onChange={(v) => setForm((f) => ({ ...f, hide_phone: v }))}
                      testid="my-profile-hide-phone"
                    />
                  </label>
                </div>

                {/* iter-99 — Bring-Your-Own AI Credentials.
                    Each user can paste their own OpenAI / Anthropic /
                    Gemini key here. When admin has flipped
                    ai_settings.byo_required to true, these keys are the
                    ONLY way AI features will work for this user.
                    Saved to users.ai_keys via /api/auth/me PUT.
                    Empty string clears that provider's key. */}
                <AICredentialsSection token={token} onSaved={() => toast.success('AI credentials saved')} />
              </div>

              <div className="flex gap-2 mt-6">
                <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5" style={{ backgroundColor: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}>
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={submitting}
                  data-testid="my-profile-submit"
                  className="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold text-white flex items-center justify-center gap-1.5 disabled:opacity-60"
                  style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
                >
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>

      {/* iter-86 — Email change is a nested 2-step modal. On success we
          force a logout-style state reset so the user is prompted to
          relogin with the new address (the server has already
          invalidated the old JWT via jwt_invalidated_at). */}
      <ChangeEmailDialog
        open={emailChangeOpen}
        onClose={() => setEmailChangeOpen(false)}
        currentEmail={me?.email}
        onRotated={() => {
          // Close all dialogs and bounce to the auth page.
          setEmailChangeOpen(false);
          onClose();
          try { window.location.assign('/auth'); } catch { /* noop */ }
        }}
      />
    </Dialog>
  );
}


/* iter-99 — AI Credentials section. Lets the signed-in user store their
 * OWN OpenAI / Anthropic / Gemini API keys so AI calls route through
 * their personal provider account instead of Emergent's universal key.
 * Required when admin has set ai_settings.byo_required=true (Hyatt-grade
 * data routing). Empty string clears that provider's key. */
function AICredentialsSection({ token, onSaved }) {
  const PROVIDERS = [
    { id: 'openai',    label: 'OpenAI',     hint: 'sk-...',          docs: 'https://platform.openai.com/api-keys' },
    { id: 'anthropic', label: 'Anthropic',  hint: 'sk-ant-...',      docs: 'https://console.anthropic.com/settings/keys' },
    { id: 'gemini',    label: 'Google Gemini', hint: 'AIza...',      docs: 'https://aistudio.google.com/app/apikey' },
  ];
  const [keys, setKeys] = useState({ openai: '', anthropic: '', gemini: '' });
  const [status, setStatus] = useState({ openai: false, anthropic: false, gemini: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        const ak = data?.ai_keys || {};
        setStatus({ openai: !!ak.openai, anthropic: !!ak.anthropic, gemini: !!ak.gemini });
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [token]);

  const handleSave = async () => {
    if (saving) return;
    const patch = {};
    for (const p of PROVIDERS) {
      const v = keys[p.id];
      if (v === '__clear__') patch[p.id] = '';
      else if (v) patch[p.id] = v;
    }
    if (Object.keys(patch).length === 0) {
      toast.info('No AI credential changes to save');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/auth/me`, { ai_keys: patch }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      const ak = data?.ai_keys || {};
      setStatus({ openai: !!ak.openai, anthropic: !!ak.anthropic, gemini: !!ak.gemini });
      setKeys({ openai: '', anthropic: '', gemini: '' });
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save keys');
    } finally { setSaving(false); }
  };

  return (
    <div className="sm:col-span-2 p-4 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid #EAE5D9' }} data-testid="my-profile-ai-credentials">
      <div className="flex items-start gap-2 mb-3">
        <Key className="w-3.5 h-3.5 mt-0.5" style={{ color: '#8B5CF6' }} />
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
            AI Credentials (BYO)
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
            Bring your own provider keys so AI calls route under your account. Required when admins enable &ldquo;Require BYO keys&rdquo; under <em>Admin → AI Settings</em>.
          </p>
        </div>
      </div>

      <div className="space-y-2.5">
        {PROVIDERS.map((p) => (
          <div key={p.id} className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                {p.label}
                {status[p.id] && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider" style={{ background: '#DCFCE7', color: '#15803D' }}>
                    ✓ Saved
                  </span>
                )}
              </label>
              <a href={p.docs} target="_blank" rel="noreferrer noopener" className="text-[10px] underline" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
                Get key →
              </a>
            </div>
            <div className="flex gap-1.5">
              <input
                type="password"
                placeholder={status[p.id] ? '••••••••••• (saved — paste new to replace)' : p.hint}
                value={keys[p.id] === '__clear__' ? '' : keys[p.id]}
                onChange={(e) => setKeys((k) => ({ ...k, [p.id]: e.target.value }))}
                data-testid={`my-profile-ai-key-${p.id}`}
                className="flex-1 text-[11px] px-2 py-1.5 rounded border"
                style={{ borderColor: '#A3D4D4', fontFamily: 'monospace', background: 'white' }}
                autoComplete="off"
              />
              {status[p.id] && (
                <button
                  type="button"
                  onClick={() => setKeys((k) => ({ ...k, [p.id]: '__clear__' }))}
                  data-testid={`my-profile-ai-key-clear-${p.id}`}
                  className="text-[10px] font-bold px-2 py-1 rounded"
                  style={{ background: '#FEE2E2', color: '#991B1B', fontFamily: 'Manrope' }}
                  title="Clear this provider's saved key"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        data-testid="my-profile-ai-keys-save"
        className="mt-3 w-full text-[11px] font-bold py-1.5 rounded-lg text-white disabled:opacity-50"
        style={{ background: '#8B5CF6', fontFamily: 'Manrope' }}
      >
        {saving ? 'Saving…' : 'Save AI credentials'}
      </button>

      <p className="text-[10px] mt-2" style={{ color: '#9CA3AF', fontFamily: 'Manrope' }}>
        🔒 Keys are stored on the server and never returned in API responses other than as &ldquo;✓ Saved&rdquo; flags.
      </p>
    </div>
  );
}


/* iter-92 — Small accessible toggle pill used by the privacy section.
   Switches a slider knob between off (gray) and on (blue) states. */
function PrivacyToggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className="relative inline-flex flex-shrink-0 h-5 w-9 rounded-full transition-colors duration-200 ease-in-out outline-none focus:ring-2 focus:ring-offset-1"
      style={{ backgroundColor: checked ? '#003DA5' : '#CBD5E1' }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out"
        style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)', marginTop: 2 }}
      />
    </button>
  );
}
